/**
 * 「Kumo からのお知らせ」の欄をこのゲームで出すかの切り替え（config の `kumoNews.optIn`・既定 ON・2026-09-27 creator/news-optout）。
 * 概要のページで機能の ON/OFF（FeaturesCard）の下に置く。
 *
 * founder 決定（2026-09-27・MASTER-PLAN §1c）: 既定で出し、クリエイターがダッシュボードで外せる（以前の「外せない」を改めた）。
 * WHY FeaturesCard の行に混ぜないか: あちらはサーバーの機能の登録簿（features.ts）の一覧で「すべて既定 OFF・ゲームの機能」。
 *   この欄は Kumo 自身のもので既定 ON・ゲームの機能ではない = 同じ表に並べると「既定 OFF」の説明と食い違う。
 * WHY 機能ページ（メール）ではなく概要か: メールのページは features.mail が OFF だとそこで止まる（ゲームのお知らせの話）。
 *   Kumo のお知らせは features.mail と無関係に出るので、ゲームの設定のスイッチが並ぶ概要に置く。
 *
 * 反映の形は FeaturesCard と同じ（切り替え専用の API は作らない）:
 *   確認ダイアログ（本番はプロジェクトの slug を打つ強い確認）→ 今の config を取り直す（版が変わっていたら止める）→
 *   applyChanges で kumoNews.optIn だけ書き換えて PUT …/config → 手元に入れるコマンド（CLI の features on|off kumoNews）を出す。
 *   正は手元の kumo.config.json（次の push で黙って戻らないように、同じ変更を手元にも入れてもらう）。
 */
import { useState } from 'react';
import { api, envPath, type ApiError, type Issue } from '../api';
import { CLI_NAME } from '../cliCommand';
import { applyChanges, localFix, pushCommand, strongConfirmOk } from '../featureToggle';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { kumoNewsChange, kumoNewsOn, KUMO_NEWS_PATH } from '../kumoNews';
import { CodeBlock, ConfirmDialog, ErrorState, Spinner } from './ui';

type Env = 'development' | 'production';

export function KumoNewsCard({ projectId, slug, env }: { projectId: string; slug: string; env: Env }) {
  const base = envPath(projectId, env);
  const r = useResource<{ version: number; config: unknown }>(`${base}/config`, 30_000);
  const { t, tr } = useT();
  // next = 確認ダイアログで確かめている変更先（null = 閉じている）
  const [next, setNext] = useState<boolean | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; issues: Issue[] } | null>(null);
  const [done, setDone] = useState<{ on: boolean; version: number; changed: boolean } | null>(null);

  const on = r.data ? kumoNewsOn(r.data.config) : null;
  const strong = env === 'production';
  const envName = t(`env.inline.${env}`);

  const open = (value: boolean) => {
    setNext(value);
    setTyped('');
    setErr(null);
    setDone(null);
  };

  const apply = async () => {
    if (next === null || !r.data) return;
    setBusy(true);
    setErr(null);
    try {
      // 反映の直前に取り直す（画面を開いた後に CLI で push されていたら、その内容を消さないように）
      const cur = await api<{ version: number; config: unknown }>(`${base}/config`);
      if (cur.version !== r.data.version) {
        setErr({ text: t('features.stale', { version: String(cur.version) }), issues: [] });
        r.reload();
        return;
      }
      const res = await api<{ version: number; changed: boolean }>(`${base}/config`, { method: 'PUT', body: applyChanges(cur.config, [kumoNewsChange(next)]) });
      setDone({ on: next, version: res.version, changed: res.changed });
      setNext(null);
      r.reload();
    } catch (e) {
      const ae = e as ApiError;
      setErr({ text: errorText(ae), issues: ae.issues });
    } finally {
      setBusy(false);
    }
  };

  const fix = done ? localFix([kumoNewsChange(done.on)]) : null;

  return (
    <section className="card" aria-label={t('kumoNews.title')}>
      <div className="card-head">
        <h2 className="card-title">{t('kumoNews.title')}</h2>
      </div>
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('kumoNews.title')} onRetry={r.reload} />}
      {r.data && on !== null && (
        <>
          <p className="muted small">{t('kumoNews.body')}</p>
          <p className="muted small">{t('kumoNews.adultsNote')}</p>
          {done && fix && (
            <div className="alert alert-ok" role="status">
              <strong>{done.changed ? t('features.doneTitle', { version: String(done.version) }) : t('features.doneSame')}</strong>
              {done.changed && (
                <div className="feature-fix">
                  <span>{tr('features.localFix', { file: <code>kumo.config.json</code> })}</span>
                  <CodeBlock code={[...fix.cli, pushCommand(env)].join('\n')} />
                </div>
              )}
            </div>
          )}
          <ul className="feature-list">
            <li className="feature-row">
              <span className={`pill ${on ? 'pill-ok' : ''}`}>{on ? t('features.on') : t('features.off')}</span>
              <div className="feature-name">
                <strong>{t('kumoNews.label')}</strong>
                <code className="small feature-path">{KUMO_NEWS_PATH}</code>
              </div>
              <div className="feature-action">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(!on)}>
                  {on ? t('kumoNews.turnOff') : t('kumoNews.turnOn')}
                </button>
              </div>
            </li>
          </ul>
        </>
      )}

      <ConfirmDialog
        open={next !== null}
        title={t(next ? 'kumoNews.confirmOnTitle' : 'kumoNews.confirmOffTitle', { env: envName })}
        confirmLabel={t(next ? 'kumoNews.confirmOn' : 'kumoNews.confirmOff', { env: envName })}
        // 本番はプレイヤーに届く = 赤で Cancel にフォーカス（FeaturesCard と同じ）。外すのはプレイヤーの害にならないので dev は通常の色
        tone={strong ? 'danger' : 'default'}
        busy={busy}
        confirmDisabled={strong && !strongConfirmOk(typed, slug)}
        onClose={() => {
          if (!busy) setNext(null);
        }}
        onConfirm={() => void apply()}
      >
        {next !== null && (
          <>
            <p>{t(next ? 'kumoNews.confirmOnBody' : 'kumoNews.confirmOffBody', { env: envName })}</p>
            <p className="small">
              <code className="small">{KUMO_NEWS_PATH}</code> <span className="muted">{next ? 'false → true' : 'true → false'}</span>
            </p>
            <p className="small muted">{tr('features.localNote', { file: <code>kumo.config.json</code>, push: <code>{CLI_NAME} config push</code> })}</p>
            {strong && (
              <label className="field">
                <span>{tr('features.typeSlug', { slug: <code>{slug}</code> })}</span>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-invalid={typed.length > 0 && !strongConfirmOk(typed, slug)}
                />
              </label>
            )}
          </>
        )}
        {err && (
          <div className="alert alert-error" role="alert">
            <span>{err.text}</span>
            {err.issues.length > 0 && (
              <ul className="small">
                {err.issues.map((i) => (
                  <li key={`${i.path}:${i.message}`}>
                    <code>{i.path}</code> {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ConfirmDialog>
    </section>
  );
}
